import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Search, Plus, Eye, Edit, Trash2, Download, RefreshCw, CheckCircle,
  MessageCircle, Loader2, BarChart3, IndianRupee, Tag, AlertTriangle,
  Users, TrendingUp, ChevronLeft, ChevronRight, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import InfluencerCreateDialog from '../pages/Influencercreatedialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate  = iso => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const PAGE_SIZE = 10;

export default function InfluencerManagement({ token, cities }) {
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [influencers, setInfluencers]   = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [page, setPage]                 = useState(0);

  const [search, setSearch]             = useState('');
  const [filterCity, setFilterCity]     = useState('');
  const [sortBy, setSortBy]             = useState('created_at');

  const [showCreate, setShowCreate]     = useState(false);
  const [showDetail, setShowDetail]     = useState(false);
  const [selectedInfl, setSelectedInfl] = useState(null);
  const [detailData, setDetailData]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showSettleDialog, setShowSettleDialog] = useState(false);
  const [settleForm, setSettleForm]     = useState({ amount: '', note: '' });
  const [settleLoading, setSettleLoading] = useState(false);

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm]         = useState({});
  const [editLoading, setEditLoading]   = useState(false);

  const fetchInfluencers = useCallback(async (resetPage = false) => {
    setLoading(true);
    try {
      const currentPage = resetPage ? 0 : page;
      const params = new URLSearchParams({
        skip: currentPage * PAGE_SIZE,
        limit: PAGE_SIZE,
        sort_by: sortBy,
        ...(search && { search }),
        ...(filterCity && { city: filterCity }),
      });
      const res = await axios.get(`${API}/superadmin/influencers?${params}`, authHeaders);
      setInfluencers(res.data.influencers || []);
      setTotal(res.data.total || 0);
      if (resetPage) setPage(0);
    } catch { toast.error('Failed to load influencers'); }
    finally { setLoading(false); }
  }, [page, search, filterCity, sortBy, token]);

  useEffect(() => { fetchInfluencers(); }, [page]);
  useEffect(() => { fetchInfluencers(true); }, [search, filterCity, sortBy]);

  const openDetail = async (infl) => {
    setSelectedInfl(infl);
    setShowDetail(true);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/influencers/${infl.id}`, authHeaders);
      setDetailData(res.data);
    } catch { toast.error('Failed to load detail'); }
    finally { setDetailLoading(false); }
  };

  const openEdit = (infl) => {
    setSelectedInfl(infl);
    const coupon = infl.coupons?.[0] || {};
    setEditForm({
      first_name: infl.first_name || '',
      last_name: infl.last_name || '',
      phone: infl.phone || '',
      city: infl.city || '',
      coupon_code: coupon.code || '',
      vehicle_types: coupon.vehicle_types || [],
      discount_percentage: coupon.discount_value || '',
      payout_cycle_days: infl.payout_cycle_days || 30,
      password: '',
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (inflId) => {
    if (!window.confirm('Delete this Growth Partner? Their coupons will be deactivated.')) return;
    try {
      await axios.delete(`${API}/superadmin/influencers/${inflId}`, authHeaders);
      toast.success('Influencer deleted');
      fetchInfluencers(true);
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = async () => {
    setEditLoading(true);
    try {
      await axios.put(`${API}/superadmin/influencers/${selectedInfl.id}`, {
        ...editForm,
        discount_percentage: parseFloat(editForm.discount_percentage),
        payout_cycle_days: parseInt(editForm.payout_cycle_days),
      }, authHeaders);
      toast.success('Influencer updated');
      fetchInfluencers(true);
      setShowEditDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setEditLoading(false); }
  };

  const handleSettle = async () => {
    const amount = parseFloat(settleForm.amount);
    if (isNaN(amount) || amount <= 0) { toast.error('Enter a valid amount'); return; }
    setSettleLoading(true);
    try {
      await axios.post(
        `${API}/superadmin/influencers/${selectedInfl.id}/settle-payout`,
        { influencer_id: selectedInfl.id, amount, note: settleForm.note },
        authHeaders
      );
      toast.success('Payout settled successfully');
      setShowSettleDialog(false);
      setSettleForm({ amount: '', note: '' });
      openDetail(selectedInfl);
    } catch { toast.error('Failed to settle payout'); }
    finally { setSettleLoading(false); }
  };

  const buildWhatsApp = (infl) => {
    const coupon = infl.coupons?.[0] || {};
    const msg = `Hello ${infl.first_name} ${infl.last_name},\n\nGreetings from X7 Autoz! 🚗\n\nWe have successfully registered you as a Growth Partner for ${infl.city} city.\n\nYour Unique ID: *${infl.influencer_uid}*\nPlease save this unique ID for future reference.\n\nYour Coupon Code: *${coupon.code || ''}*\nDiscount Percentage: *${coupon.discount_value || ''}%*\n\nTo login:\nwww.x7autoz.com/login\n\nWelcome to the X7 Autoz family!\n\nBest regards,\nX7 Autoz Team`;
    const phone = (infl.phone || '').replace(/\D/g, '');
    const intl = phone.startsWith('91') ? phone : `91${phone}`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const downloadCSV = () => {
    if (!influencers.length) { toast.error('No data to download'); return; }
    const rows = influencers.map(i => ({
      'Influencer ID': i.influencer_uid,
      'Name': i.name,
      'Email': i.email,
      'Phone': i.phone,
      'City': i.city,
      'Coupon Code': i.coupons?.[0]?.code || '',
      'Discount %': i.coupons?.[0]?.discount_value || '',
      'Vehicle Types': (i.coupons?.[0]?.vehicle_types || []).join(', '),
      'Payout Cycle': `${i.payout_cycle_days} days`,
      'Times Used': i.total_coupon_used,
      'Total Earnings': i.total_earnings,
      'This Month Earnings': i.month_earnings,
      'Pending Payout': i.pending_payout,
      'Payout Status': i.payout_status,
      'Joined': fmtDate(i.joined_date || i.created_at),
    }));
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'influencers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Growth Partner Management
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Manage influencers/growth partners — coupon codes, earnings, payouts
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => fetchInfluencers(true)} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button variant="outline" onClick={downloadCSV}>
                  <Download className="h-4 w-4 mr-1" /> CSV
                </Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => setShowCreate(true)}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Growth Partner
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name, email, ID, phone..."
                  className="pl-9"
                />
              </div>
              <Select value={filterCity || 'all'} onValueChange={v => setFilterCity(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Cities" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]"><SelectValue placeholder="Sort by" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Newest First</SelectItem>
                  <SelectItem value="discount_percentage">Highest Discount</SelectItem>
                  <SelectItem value="total_earnings">Highest Earnings</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-gray-400">{total} total Growth Partners</p>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="text-center py-16"><Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-400" /></div>
          ) : influencers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-500">No growth partners found.</p>
              <Button className="mt-4 bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add First Partner
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-gray-600 font-medium">Partner</th>
                      <th className="text-left p-3 text-gray-600 font-medium">Coupon</th>
                      <th className="text-left p-3 text-gray-600 font-medium">City</th>
                      <th className="text-left p-3 text-gray-600 font-medium">Times Used</th>
                      <th className="text-left p-3 text-gray-600 font-medium">Total Earned</th>
                      <th className="text-left p-3 text-gray-600 font-medium">This Month</th>
                      <th className="text-left p-3 text-gray-600 font-medium">Payout</th>
                      <th className="text-left p-3 text-gray-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {influencers.map((infl) => {
                      const coupon = infl.coupons?.[0] || {};
                      return (
                        <tr key={infl.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-semibold text-sm flex-shrink-0">
                                {infl.first_name?.charAt(0)}{infl.last_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{infl.name}</p>
                                <p className="text-xs text-gray-400 font-mono">{infl.influencer_uid}</p>
                                <p className="text-xs text-gray-400">{infl.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-purple-700 font-bold text-xs bg-purple-50 px-2 py-0.5 rounded">{coupon.code || '—'}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{coupon.discount_value}% off</p>
                            <p className="text-xs text-gray-400">{(coupon.vehicle_types || []).join(', ')}</p>
                          </td>
                          <td className="p-3">
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{infl.city}</span>
                          </td>
                          <td className="p-3">
                            <p className="font-semibold text-gray-800">{infl.total_coupon_used || 0}</p>
                            <p className="text-xs text-gray-400">transactions</p>
                          </td>
                          <td className="p-3">
                            <p className="font-semibold text-green-700">{fmtMoney(infl.total_earnings)}</p>
                            <p className="text-xs text-gray-400">lifetime</p>
                          </td>
                          <td className="p-3">
                            <p className="font-semibold text-blue-700">{fmtMoney(infl.month_earnings)}</p>
                          </td>
                          <td className="p-3">
                            <div>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                infl.payout_status === 'settled'
                                  ? 'bg-green-100 text-green-700'
                                  : infl.pending_payout > 0
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-gray-100 text-gray-600'
                              }`}>
                                {infl.payout_status === 'settled' ? '✓ Settled' : infl.pending_payout > 0 ? `${fmtMoney(infl.pending_payout)} due` : 'No pending'}
                              </span>
                              <p className="text-xs text-gray-400 mt-0.5">{infl.payout_cycle_days}d cycle</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => openDetail(infl)} title="View Details">
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => openEdit(infl)} title="Edit">
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => buildWhatsApp(infl)} title="WhatsApp" className="text-green-600 border-green-200">
                                <MessageCircle className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="destructive" size="sm" onClick={() => handleDelete(infl.id)} title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
                <p className="text-xs text-gray-500">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                  </Button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pageNum = Math.max(0, Math.min(page - 2 + i, totalPages - 1));
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className={pageNum === page ? 'bg-purple-600 text-white' : ''}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── CREATE DIALOG ── */}
      <InfluencerCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        cities={cities}
        token={token}
        onCreated={() => fetchInfluencers(true)}
      />

      {/* ── DETAIL DIALOG ── */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Growth Partner Details</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="text-center py-16"><Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-400" /></div>
          ) : detailData && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center gap-4 bg-purple-50 rounded-xl p-4">
                <div className="w-14 h-14 rounded-full bg-purple-200 flex items-center justify-center text-purple-800 font-bold text-xl">
                  {detailData.influencer?.first_name?.charAt(0)}{detailData.influencer?.last_name?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{detailData.influencer?.name}</h2>
                  <p className="font-mono text-purple-700 text-sm">{detailData.influencer?.influencer_uid}</p>
                  <p className="text-gray-500 text-sm">{detailData.influencer?.email} · {detailData.influencer?.phone}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{detailData.influencer?.city}</span>
                    <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">{detailData.influencer?.payout_cycle_days}d cycle</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Joined {fmtDate(detailData.influencer?.joined_date || detailData.influencer?.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Analytics cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Total Transactions', value: detailData.analytics?.total_usages, color: 'text-gray-800', bg: 'bg-gray-50' },
                  { label: 'Total Earned', value: fmtMoney(detailData.analytics?.total_earnings), color: 'text-green-700', bg: 'bg-green-50' },
                  { label: 'This Month', value: fmtMoney(detailData.analytics?.this_month_earnings), color: 'text-blue-700', bg: 'bg-blue-50' },
                  { label: 'Pending Payout', value: fmtMoney(detailData.analytics?.pending_payout), color: 'text-amber-700', bg: 'bg-amber-50' },
                  { label: '30-Day Earnings', value: fmtMoney(detailData.analytics?.last_30_days_earnings), color: 'text-indigo-700', bg: 'bg-indigo-50' },
                  { label: '15-Day Earnings', value: fmtMoney(detailData.analytics?.last_15_days_earnings), color: 'text-teal-700', bg: 'bg-teal-50' },
                  { label: 'Total Settled', value: fmtMoney(detailData.analytics?.total_settled), color: 'text-gray-700', bg: 'bg-gray-50' },
                  { label: 'Most Used Vehicle', value: detailData.analytics?.most_used_vehicle_type || '—', color: 'text-orange-700', bg: 'bg-orange-50' },
                ].map((s, i) => (
                  <div key={i} className={`rounded-xl border p-3 ${s.bg}`}>
                    <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                    <p className={`font-bold text-sm ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Coupon cards */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Coupon Codes</p>
                <div className="space-y-2">
                  {(detailData.coupons || []).map(c => (
                    <div key={c.id} className="flex items-center justify-between p-3 border rounded-xl bg-white">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-purple-700 bg-purple-50 px-3 py-1 rounded-lg text-sm">{c.code}</span>
                        <div>
                          <p className="text-sm font-medium">{c.discount_value}% discount</p>
                          <p className="text-xs text-gray-400">{(c.vehicle_types || []).join(', ')} · Used {c.used_count} times</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {c.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vehicle type breakdown */}
              {Object.keys(detailData.analytics?.vehicle_type_breakdown || {}).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vehicle Type Usage</p>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(detailData.analytics.vehicle_type_breakdown).map(([vtype, count]) => (
                      <div key={vtype} className="border rounded-xl p-3 text-center bg-gray-50">
                        <p className="text-lg">{vtype === 'car' ? '🚗' : vtype === 'bike' ? '🏍️' : vtype === 'ev' ? '⚡' : '🚛'}</p>
                        <p className="text-xs text-gray-500 capitalize mt-1">{vtype}</p>
                        <p className="font-bold text-gray-800">{count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payout history */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Payout History</p>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white text-xs"
                    onClick={() => { setShowSettleDialog(true); }}
                  >
                    <IndianRupee className="h-3.5 w-3.5 mr-1" /> Settle Payout
                  </Button>
                </div>
                {(detailData.payout_history || []).length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No payouts settled yet.</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {detailData.payout_history.map(p => (
                      <div key={p.id} className="flex items-center justify-between p-2.5 border rounded-lg text-sm bg-gray-50">
                        <div>
                          <p className="font-medium">{fmtMoney(p.amount)}</p>
                          <p className="text-xs text-gray-400">{fmtDate(p.created_at)}{p.note ? ` · ${p.note}` : ''}</p>
                        </div>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Settled</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── SETTLE PAYOUT DIALOG ── */}
      <Dialog open={showSettleDialog} onOpenChange={setShowSettleDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Settle Payout</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Pending: <strong className="text-amber-600">{fmtMoney(detailData?.analytics?.pending_payout || 0)}</strong>
            </p>
            <div>
              <Label>Amount to Settle (₹)</Label>
              <Input
                type="number"
                value={settleForm.amount}
                onChange={e => setSettleForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input
                value={settleForm.note}
                onChange={e => setSettleForm(f => ({ ...f, note: e.target.value }))}
                placeholder="e.g. UPI transfer"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowSettleDialog(false)}>Cancel</Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleSettle}
                disabled={settleLoading}
              >
                {settleLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Confirm Settlement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── EDIT DIALOG ── */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Growth Partner</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name</Label>
                <Input value={editForm.first_name || ''} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input value={editForm.last_name || ''} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
            </div>
            <div>
              <Label>City</Label>
              <Select value={editForm.city} onValueChange={v => setEditForm(f => ({ ...f, city: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{cities.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Coupon Code (6–8 chars)</Label>
              <Input
                className="font-mono uppercase"
                maxLength={8}
                value={editForm.coupon_code || ''}
                onChange={e => setEditForm(f => ({ ...f, coupon_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
              />
            </div>
            <div>
              <Label>Discount % on Base Price</Label>
              <Input
                type="number" min="1" max="100"
                value={editForm.discount_percentage || ''}
                onChange={e => setEditForm(f => ({ ...f, discount_percentage: e.target.value }))}
              />
            </div>
            <div>
              <Label>Payout Cycle</Label>
              <div className="flex gap-3">
                {[15, 30].map(d => (
                  <button key={d} type="button"
                    onClick={() => setEditForm(f => ({ ...f, payout_cycle_days: d }))}
                    className={`flex-1 py-2 rounded-xl border text-sm font-medium ${editForm.payout_cycle_days === d ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                  >
                    {d} days
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>New Password (leave blank to keep)</Label>
              <Input type="password" value={editForm.password || ''} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={handleEdit} disabled={editLoading}>
                {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}